<template>
  <Slideover :visible="visible" size="max-w-4xl">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="close()"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Slideover Body -->
    <div class="relative px-4 py-2">
      <div class="space-y-2" v-if="orderDetails.length">
        <div class="flex flex-col">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg"
              >
                <table class="min-w-full divide-y divide-gray-200">
                  <thead>
                   <tr>
                      <th>Variant</th>
                      <th>Product</th>
                      <th>Size</th>
                      <th>ESD</th>
                      <th>Print Date</th>
                      <th>Ord</th>
                      <th>Com</th>
                      <th>Need</th>
                    </tr>
                  </thead>
                  <tbody
                    class="bg-white divide-y divide-gray-200 align-text-top"
                  >
                    <tr v-for="(product, index) in orderDetails" :key="index">
                      <td>
                        {{ product.variant_id }}
                      </td>
                      <td>
                        {{ product.variant.product.name }}
                      </td>
                      <td>
                        {{ product.variant.variant_name }}
                      </td>
                      <td>
                        {{ product.ExpectedShipDate | date }}
                      </td>
                      <td>
                        <div class="w-32 m-auto">
                          <div
                            v-if="disablePrintDate(product)"
                            class="text-center"
                          >
                            {{ disableText }}
                          </div>
                          <v-date-picker
                            v-else
                            v-model="product.jsDateShipped"
                            color="green"
                            @input="appendDates($event, product.ItemNumber)"
                          />
                        </div>
                      </td>
                      <td class="text-center">
                        {{ product.QuantityOrdered }}
                      </td>
                      <td class="text-center">
                        {{ product.QuantityShipped }}
                      </td>
                      <td class="text-center">
                        {{ product.QuantityNeeded }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="py-4 space-y-4">
        <!-- Note -->
        <div>
          <label
            :for="`note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Note
          </label>
          <div class="mt-1 rounded-md shadow-sm">
            <input
              :id="`note-${_uid}`"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              v-model="note"
            />
          </div>
        </div>
      </div>

      <!-- Errors -->
      <HttpError :error="error" />
      <OrderError :errors="errors" errorMessage="Edit Print Date Errors" />

      <!-- Buttons -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <Button class="btn btn-white" v-on:click-handler="close()">
            Cancel
          </Button>
        </span>

        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <Button
            v-on:click-handler="editPrintDates()"
            :disabled="saving || note == null || !products.length"
            class="flex items-center space-x-2"
          >
            <span>Save</span>
            <LoadingSpinner v-if="saving" class="text-white" />
          </Button>
        </span>
      </div>
    </div>
  </Slideover>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import OrderError from "@/components/elements/OrderError";

const OrderApi = ApiFactory.get("orders");

export default {
  name: "OrderEditPrintDatesVBI",
  components: {
    OrderError,
    Slideover,
    Button,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saving: false,
      reasons: [],
      reasonsLoaded: false,
      processing: false,
      error: null,
      clearPrintDates: [],
      note: null,
      adjustInventory: true,
      products: [],
      errors: [],
    };
  },
  mixins: [dateFormatter],
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    orderDetails() {
      if (!this.order.details) {
        return [];
      }
      return this.order.details
        .filter((product) => {
          if (this.isProductCancelled(product)) {
            return false;
          }

          if (product.Adjustment != "0") {
            return false;
          }

          if (product.Type == "P") {
            return false;
          }

          let sku = product.SKU.toLowerCase();
          for (let item of ["warranty", "giftcert", "giftwrap", "routeins"]) {
            if (sku.includes(item)) {
              return false;
            }
          }

          return true;
        })
        .map((product) => {
          product.children = this.order.details.filter(
            (child) => child.Type == "P" && product.ItemNumber == child.Parent
          );
          product.jsDateShipped = product.DateShipped
            ? new Date(product.DateShipped)
            : null;
          return product;
        });
    },
    disableText() {
      return "Hasn't Printed";
    },
  },
  mounted() {},
  methods: {
    disablePrintDate(product) {
      return product?.QuantityNeeded > 0 || !product?.DateShipped;
    },
    isProductCancelled(product) {
      return (
        product?.Canceled === 1 || product?.Product?.search(/^CANCELED/g) > -1
      );
    },
    appendDates(e, itemNumber) {
      this.products = this.products.filter((product) => {
        return product.ItemNumber != itemNumber;
      });
      this.products.push({
        ItemNumber: itemNumber,
        DateShipped: this.$options.filters.date(e),
      });
    },
    close() {
      if (this.errors?.length) {
        this.$emit("toggle-slideover");
        this.$emit("refresh-order");
      } else {
        this.$emit("toggle-slideover");
      }
    },
    editPrintDates() {
      this.saving = true;
      this.error = null;

      OrderApi.updatePrintDates(this.order.SourceOrderNumber, {
        products: this.products,
        note: this.note,
        adjustInventory: this.adjustInventory,
      })
        .then((response) => {
          this.errors = response?.data?.body?.errors;
          if (!this.errors?.length) {
            this.$emit("toggle-slideover");
            this.$emit("refresh-order");
          }
        })
        .catch((e) => {
          this.error = e;
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
  filters: {
    date: dateFormatter.format,
  },
};
</script>
<style scoped lang="scss">
th {
  @apply px-2 py-2 bg-gray-200 text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider;
}

td {
  @apply px-2 py-1 whitespace-no-wrap text-sm leading-5;
}

table {
  @apply text-gray-500;
}
</style>
